/** @jsxRuntime automatic */
import { useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Collapse } from "./collapse";
import { SectionHeading } from "./heading-level";
import { cn } from "./utils";

export interface DisclosureProps {
  /**
   * Row title: a short noun ("Detalles del envío", "Carta de presentación")
   * or richer header content (an FAQ question, a skill with its badge). It
   * renders in body/semibold; nested elements may set their own type.
   */
  title: ReactNode;
  /** Right-aligned metadata before the chevron (a Badge "Preparada", a count, hours). */
  meta?: ReactNode;
  /** Leading icon (an `<Icon>`). */
  icon?: ReactNode;
  /** Secondary line under the title. */
  description?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Controlled mode. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `card`: bordered card. `plain`: a row inside another surface, no side
   * padding. `row`: a padded row inside a `divide-y` list or a Card (Ayuda,
   * Claves de API, Plan de aprendizaje).
   */
  variant?: "card" | "plain" | "row";
  /** id of the root (deep links like `#q-3`; the root keeps `scroll-mt-20`). */
  id?: string;
  className?: string;
  /** Classes for the content area. */
  contentClassName?: string;
  /** Heading level wrapping the trigger (default: the section level, h3 outside a `SectionHeadingLevelProvider`). */
  as?: "h2" | "h3" | "h4";
  /** Opens and closes with no motion (many at once: Help's search opens every match). */
  instant?: boolean;
  "data-testid"?: string;
}

/**
 * The single expand/collapse pattern (thread-16, secondary-16): a full-width
 * clickable row, title + meta + a 16 px chevron that rotates 180°, with
 * `aria-expanded` / `aria-controls`. The body is a `Collapse` (spec 07 ›
 * Movimiento): it grows in 200 ms `--ease-move` while its content arrives,
 * and folds with the content out in 80 and the rows in 130, so the content
 * below slides instead of jumping (with reduced motion the rows change at
 * once and the content fades). It is mounted only while open or folding,
 * mounts open at rest (`defaultOpen`, a deep link), and while it folds the
 * focus inside goes back to the row and the body is `inert`.
 */
export function Disclosure({
  title,
  meta,
  icon,
  description,
  children,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  variant = "card",
  id,
  className,
  contentClassName,
  as: headingProp,
  instant = false,
  "data-testid": testId,
}: DisclosureProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const contentId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  return (
    <div
      id={id}
      data-testid={testId}
      data-state={open ? "open" : "closed"}
      className={cn("scroll-mt-20", variant === "card" && "rounded-lg border border-border bg-card", className)}
    >
      <SectionHeading as={headingProp} className="m-0">
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={toggle}
          // A row's press (`press-row`); `plain` paints no fill, so it is not one.
          data-press-row={variant === "plain" ? undefined : ""}
          className={cn(
            "group flex min-h-11 w-full cursor-pointer items-center gap-3 text-left press-row hover:bg-hover active:bg-pressed focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
            variant === "card" && "rounded-lg px-4 py-2.5",
            variant === "card" && open && "rounded-b-none",
            variant === "plain" && "px-0 py-2.5 hover:bg-transparent active:bg-transparent",
            variant === "row" && "px-4 py-3.5",
          )}
        >
          {icon ? <span className="flex shrink-0 text-muted-foreground">{icon}</span> : null}
          <span className="min-w-0 flex-1">
            <span className="block text-body font-semibold text-foreground">{title}</span>
            {description ? <span className="block text-label text-muted-foreground">{description}</span> : null}
          </span>
          {meta ? <span className="flex shrink-0 items-center gap-2 text-label text-muted-foreground">{meta}</span> : null}
          <ChevronDown
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            className={cn(
              "shrink-0 text-muted-foreground transition-transform duration-base ease-move motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </button>
      </SectionHeading>
      {/* Disclosure's opening was the model of Collapse's: driven by an effect, so a
          re-render never loses it (E2E app-flows); settled, the clip comes off and a
          sticky inside sticks (thread-06). */}
      <Collapse id={contentId} open={open} appear="never" instant={instant} focusTarget={triggerRef}>
        <div
          className={cn(
            variant === "card" && "border-t border-border px-4 py-3",
            variant === "plain" && "pb-3",
            variant === "row" && "px-4 pb-4",
            contentClassName,
          )}
        >
          {children}
        </div>
      </Collapse>
    </div>
  );
}
